import { useEffect, useRef, useCallback } from 'react'
import { useUIStore } from '../store/uiStore'
import { useNotesStore } from '../store/notesStore'

interface UseAutoSaveOptions {
  noteId: string | null
  content: string
  title?: string
  enabled?: boolean
}

export const useAutoSave = ({ noteId, content, title, enabled = true }: UseAutoSaveOptions) => {
  const { autoSaveInterval, setSaveStatus, updateLastSaved } = useUIStore()
  const { updateNote } = useNotesStore()
  
  const timeoutRef = useRef<NodeJS.Timeout>()
  const lastSavedContentRef = useRef<string>('')
  const lastSavedTitleRef = useRef<string>('')

  const saveNote = useCallback(async () => {
    if (!noteId || !enabled) return

    const hasContentChanged = content !== lastSavedContentRef.current
    const hasTitleChanged = title !== lastSavedTitleRef.current

    if (!hasContentChanged && !hasTitleChanged) return

    try {
      setSaveStatus('saving')
      
      const updates: any = {}
      if (hasContentChanged) {
        updates.content = content
      }
      if (hasTitleChanged && title !== undefined) {
        updates.title = title
      }

      await updateNote(noteId, updates)
      
      lastSavedContentRef.current = content
      lastSavedTitleRef.current = title || ''
      updateLastSaved()
    } catch (error) {
      setSaveStatus('error')
      console.error('Auto-save failed:', error)
    }
  }, [noteId, content, title, enabled, updateNote, setSaveStatus, updateLastSaved])

  const debouncedSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(saveNote, autoSaveInterval * 300)
  }, [saveNote, autoSaveInterval])

  const saveImmediately = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    saveNote()
  }, [saveNote])

  // Auto-save when content or title changes
  useEffect(() => {
    if (!enabled || !noteId) return

    const hasContentChanged = content !== lastSavedContentRef.current
    const hasTitleChanged = title !== lastSavedTitleRef.current

    if (hasContentChanged || hasTitleChanged) {
      debouncedSave()
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [content, title, debouncedSave, enabled, noteId])

  // Update refs when noteId changes (new note selected) - only set to initial values
  useEffect(() => {
    if (noteId) {
      // Only set the initial state when switching notes
      lastSavedContentRef.current = content
      lastSavedTitleRef.current = title || ''
    }
  }, [noteId]) // Only depend on noteId, not content/title

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    saveImmediately,
    saveNote
  }
}